'use client';

import React from 'react';
import Link from 'next/link';
import { cn } from '@/lib/utils';

/**
 * Tiny "D" pill flagging a ticker with a recent SEC dilutive offering filing
 * (≤30 days). Used in movers tables and watchlist tiles. Membership comes from
 * useDilutingSymbols(); render this only when the symbol is in that set.
 *
 * By default it links to the stock's detail page (where DilutionSignals shows
 * the full filing). Pass `asSpan` in contexts that already wrap the row in a
 * link/button to avoid nested interactive elements.
 */
export function DilutionBadge({
  symbol,
  asSpan = false,
  className,
}: {
  symbol?: string;
  asSpan?: boolean;
  className?: string;
}): React.JSX.Element {
  const cls = cn(
    'inline-flex h-4 min-w-[1rem] items-center justify-center rounded px-1',
    'text-[10px] font-bold leading-none border flex-shrink-0',
    'bg-red-500/20 text-red-300 border-red-500/30',
    !asSpan && 'hover:bg-red-500/30 transition-colors cursor-pointer',
    className,
  );
  const title = 'Recent SEC dilutive offering filing (≤30d) — potential share dilution';

  if (asSpan || !symbol) {
    return <span className={cls} title={title} aria-label="Recent dilutive offering filing">D</span>;
  }

  return (
    <Link
      href={`/stocks/${symbol}`}
      onClick={(e) => e.stopPropagation()}
      className={cls}
      title={title}
      aria-label={`${symbol} — recent dilutive offering filing`}
    >
      D
    </Link>
  );
}

export default DilutionBadge;
